import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Card } from "flowbite-react";
import Toggle from "../../lib/ui/components/toggle/Toggle";
import Button from "../../lib/ui/components/button/Button";
import { SmallText, XsmallText } from "../../lib/ui/components/typography/Typography";
import { GET_JOB_ATS_STAGES, UPDATE_JOB_STAGE_SETTINGS } from "../../lib/urls";
import { mergeStageOptionsWithStale } from "../../utils/jobStageSettings";

export default function JobAtsStageSettings({
    jobId,
    companyId,
    token,
    atsJobId,
    automaticInterviews,
    interviewEligibleStageNames,
    autoAdvanceTargetStage,
    autoAdvanceHighMatchStage,
    autoAdvanceMinMatchRating,
    autoAdvanceHumanInterviewStage,
    onSettingsSaved,
    onAutomaticInterviewsChange,
}) {
    const [liveStages, setLiveStages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const [eligible, setEligible] = useState(
        () => (interviewEligibleStageNames || []).map((n) => String(n).trim()).filter(Boolean)
    );
    const [completionStage, setCompletionStage] = useState(autoAdvanceTargetStage || "");
    const [highMatchStage, setHighMatchStage] = useState(autoAdvanceHighMatchStage || "");
    const [minRating, setMinRating] = useState(autoAdvanceMinMatchRating ?? 0);
    const [humanStage, setHumanStage] = useState(autoAdvanceHumanInterviewStage || "");
    const [autoAdvanceOn, setAutoAdvanceOn] = useState(Boolean(autoAdvanceTargetStage));
    const [humanOn, setHumanOn] = useState(Boolean(autoAdvanceHumanInterviewStage));

    const fetchStages = async () => {
        if (!atsJobId || !companyId || !jobId || !token) {
            setLoading(false);
            return;
        }
        setLoading(true);
        setError("");
        try {
            const response = await fetch(GET_JOB_ATS_STAGES(companyId, jobId), {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!response.ok) {
                throw new Error("Failed to load ATS stages");
            }
            const data = await response.json();
            setLiveStages(data.stages || []);
        } catch (err) {
            setError(err.message || "Failed to load ATS stages");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void fetchStages();
    }, [atsJobId, companyId, jobId, token]);

    const savedNames = useMemo(
        () =>
            [
                ...(interviewEligibleStageNames || []),
                autoAdvanceTargetStage,
                autoAdvanceHighMatchStage,
                autoAdvanceHumanInterviewStage,
            ].filter(Boolean),
        [
            interviewEligibleStageNames,
            autoAdvanceTargetStage,
            autoAdvanceHighMatchStage,
            autoAdvanceHumanInterviewStage,
        ]
    );
    const { live, stale, all } = useMemo(
        () => mergeStageOptionsWithStale(liveStages, savedNames),
        [liveStages, savedNames]
    );

    if (!atsJobId) {
        return (
            <Card className="w-full">
                <SmallText>
                    This role is not linked to an ATS job, so stage eligibility and auto-advance are not available.
                </SmallText>
            </Card>
        );
    }

    const toggleEligible = (name, next) => {
        const current = new Set(eligible);
        if (next) {
            current.add(name);
        } else {
            current.delete(name);
        }
        const nextList = Array.from(current);
        if (automaticInterviews && nextList.length === 0) {
            toast.info(
                "Automatic interviews will be turned off because June needs at least one eligible stage to send invites."
            );
        }
        setEligible(nextList);
    };

    const save = async () => {
        setSaving(true);
        try {
            const response = await fetch(UPDATE_JOB_STAGE_SETTINGS(companyId, jobId), {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    interview_eligible_stage_names: eligible,
                    auto_advance_target_stage: autoAdvanceOn ? completionStage || null : null,
                    auto_advance_high_match_stage: autoAdvanceOn ? highMatchStage || null : null,
                    auto_advance_min_match_rating: autoAdvanceOn ? minRating ?? 0 : null,
                    auto_advance_human_interview_stage: humanOn ? humanStage || null : null,
                }),
            });
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.detail || "Failed to save stage settings");
            }
            const data = await response.json();
            if (data.automatic_interviews_turned_off) {
                toast.info(
                    "Automatic interviews was turned off because no eligible stages are selected."
                );
                onAutomaticInterviewsChange?.(false);
            } else {
                toast.success("ATS stage settings saved");
            }
            onSettingsSaved?.(data);
        } catch (err) {
            toast.error(err.message || "Failed to save stage settings");
        } finally {
            setSaving(false);
        }
    };

    const stageSelect = (id, value, onChange, includeNone) => (
        <select
            id={id}
            className="border border-gray-300 rounded-md px-3 py-2 text-small"
            value={value}
            onChange={(e) => onChange(e.target.value)}
        >
            {includeNone ? <option value="">None</option> : (
                <option value="" disabled hidden>Select a stage...</option>
            )}
            {all.map((stage) => (
                <option key={stage} value={stage}>
                    {stale.includes(stage) && !live.includes(stage)
                        ? `${stage} (not in this role’s pipeline)`
                        : stage}
                </option>
            ))}
        </select>
    );

    return (
        <Card className="w-full">
            <div>
                <div className="mb-2 text-body-lg">Automatic Interview Stages</div>
                <SmallText emphasis={false} className="mb-4 block text-gray-600">
                    When Automatic Interviews is on, June sends invites to candidates who are in — or later enter — these stages.
                </SmallText>
                {loading ? (
                    <XsmallText>Loading stages from your ATS…</XsmallText>
                ) : error ? (
                    <div className="space-y-2">
                        <XsmallText className="text-red-600">{error}</XsmallText>
                        <Button variant="secondary" onClick={() => void fetchStages()}>Retry</Button>
                    </div>
                ) : (
                    <div className="flex flex-col gap-3">
                        {all.map((stage) => (
                            <div key={stage} className="flex items-start gap-3">
                                <Toggle
                                    checked={eligible.includes(stage)}
                                    onChange={(next) => toggleEligible(stage, next)}
                                />
                                <div className="min-w-0 flex-1">
                                    <SmallText emphasis={true}>{stage}</SmallText>
                                    {stale.includes(stage) && !live.includes(stage) ? (
                                        <XsmallText>No longer in this role’s ATS pipeline</XsmallText>
                                    ) : null}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="mt-8 space-y-4 border-t border-gray-200 pt-8">
                <div>
                    <div className="mb-2 text-body-lg">Automatic Stage Advancement</div>
                    <SmallText emphasis={false} className="mb-4 block text-gray-600">
                        After interviews finish or candidates ask to speak with your team, June can move them to the right stage in your ATS—without manual updates.
                    </SmallText>
                </div>
                <div className="space-y-3">
                    <div className="flex items-center gap-3">
                        <Toggle checked={autoAdvanceOn} onChange={setAutoAdvanceOn} />
                        <SmallText emphasis={true}>Auto-advance to ATS stage</SmallText>
                    </div>
                    {autoAdvanceOn ? (
                        <div className="space-y-3 pl-0 sm:pl-11">
                            <div className="flex items-center gap-2 flex-wrap">
                                <label htmlFor="jobCompletionStage" className="text-small font-medium">
                                    Completion stage
                                </label>
                                {stageSelect("jobCompletionStage", completionStage, setCompletionStage, false)}
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <label htmlFor="jobHighMatchStage" className="text-small font-medium">
                                    High-match stage
                                </label>
                                {stageSelect("jobHighMatchStage", highMatchStage, setHighMatchStage, true)}
                                <label htmlFor="jobMinMatchRating" className="text-small font-medium">
                                    Min match rating
                                </label>
                                <input
                                    id="jobMinMatchRating"
                                    type="number"
                                    min={0}
                                    max={5}
                                    className="border border-gray-300 rounded-md px-3 py-2 text-small w-20"
                                    value={minRating}
                                    onChange={(e) => setMinRating(Number(e.target.value))}
                                />
                            </div>
                        </div>
                    ) : null}
                </div>

                <div className="space-y-3 border-t border-gray-200 pt-6">
                    <div className="flex items-center gap-3">
                        <Toggle checked={humanOn} onChange={setHumanOn} />
                        <SmallText emphasis={true}>Human interview request — Auto-advance</SmallText>
                    </div>
                    {humanOn ? (
                        <div className="flex items-center gap-2 flex-wrap pl-0 sm:pl-11">
                            <label htmlFor="jobHumanStage" className="text-small font-medium">
                                Human interview stage
                            </label>
                            {stageSelect("jobHumanStage", humanStage, setHumanStage, false)}
                        </div>
                    ) : null}
                </div>
            </div>

            <div className="mt-6">
                <Button variant="primary" onClick={save} disabled={saving || loading}>
                    {saving ? "Saving..." : "Save ATS stage settings"}
                </Button>
            </div>
        </Card>
    );
}
